import type { IHttpClient } from '@wix/yoshi-flow-editor';
import { ItemsClient } from './itemsClient';
import { MenusClient } from './menusClient';
import { SectionsClient } from './sectionsClient';
import type { PopulatedMenu, Item } from '../types/menusTypes';
import {
  convertMenusToMenuType,
  convertSectionsToSectionType,
  convertPopulatedItemsToItemType,
  getItemIds,
  populateMenu,
} from './utils/utils';
import { context } from 'root/context/RootContext';
import { menusState } from 'root/states/MenusState';
import type { GetMenuIdsByOperation } from './PopulateMenuIdsByOperationClient';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';

export const PopulatedMenuClient = ({
  httpClient,
  errorHandler,
  msid = '',
  currency,
  errorMonitor,
  timezone,
  enableSectionItemAvailability,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  msid: string | undefined;
  currency: string;
  errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>;
  timezone?: string;
  enableSectionItemAvailability?: boolean;
}) => {
  const menusClient = new MenusClient(httpClient, errorHandler);
  const sectionsClient = new SectionsClient(httpClient, errorHandler);
  const itemsClient = new ItemsClient(httpClient, errorHandler);

  return {
    getPartial: async (menuIdsByOperationPromise: Promise<GetMenuIdsByOperation | undefined>) => {
      try {
        const menuIdsByOperation = (await menuIdsByOperationPromise)?.menuIdsByOperation;

        if (!menuIdsByOperation?.length) {
          errorMonitor?.addBreadcrumb({
            category: 'get menus',
            message: 'menuIds is empty for selected operation',
          });
          errorMonitor?.reportError(new Error(`menuIds is empty for ${msid}`));
          return;
        }

        const [menuResponse, sectionResponse] = await Promise.all([
          menusClient.fetchVisibleMenus(menuIdsByOperation),
          sectionsClient.fetchAllSections(),
        ]);

        const menus = convertMenusToMenuType({ menus: menuResponse.menus });
        const sections = convertSectionsToSectionType({
          sections: sectionResponse?.sections ?? [],
          timezone,
          enableSectionItemAvailability,
        });

        return menus.map((menu) => populateMenu(menu, sections)) as PopulatedMenu[];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('fetch data failed', e);
        menusState.setHasError(true);
      }
    },
    getItems: async (menus: PopulatedMenu[]): Promise<Item[]> => {
      const { items } = await itemsClient.fetchAllItems(getItemIds(menus));

      const itemsWithoutImgSize =
        items?.filter(
          (item) => item.image && item.image.id && !(item.image.width || item.image.height)
        ) || [];

      const fileIds = itemsWithoutImgSize.map((item) => item.image!.id!);
      let imagesData;
      if (fileIds.length > 0) {
        context.biReporterService?.reportOloGenericDebugBiEvent({
          subjectType: 'fix width and height of images',
          value: {
            fileIds: JSON.stringify(fileIds),
            fileIdsLength: fileIds.length,
            msid,
          },
        });
        imagesData = await itemsClient.getImgsData(fileIds, msid);
      }

      return items
        ? convertPopulatedItemsToItemType({
            items,
            currency,
            imagesData,
            timezone,
            enableSectionItemAvailability,
          })
        : [];
    },
    getMenuFirstValidItem: async (menuWithItems: PopulatedMenu): Promise<Item | undefined> => {
      const sectionWithItems = menuWithItems.sections?.find((section) => section.itemIds?.length);
      const firstItemId = sectionWithItems?.itemIds?.[0];

      if (firstItemId) {
        const item = (await itemsClient.getItemById(firstItemId)) as Item;

        const isItemWithoutImgSize =
          item.image && item.image.id && !(item.image.width || item.image.height);

        let imagesData;
        if (isItemWithoutImgSize && item.image!.id) {
          const imageId = item.image!.id;
          context.biReporterService?.reportOloGenericDebugBiEvent({
            subjectType: 'fix width and height of image',
            value: {
              fileId: imageId,
              msid,
            },
          });
          imagesData = await itemsClient.getImgsData([imageId], msid);
        }

        return item
          ? convertPopulatedItemsToItemType({
              items: [item],
              currency,
              imagesData,
              timezone,
              enableSectionItemAvailability,
            })[0]
          : undefined;
      }
    },
  };
};
